import React from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { colors } from '../../../styles/colors';

const PDFGenerator = ({
  savedHorses,
  setError
}) => {
        // Helper function to calculate application time
        const calculateApplicationTime = (runTime, hoursBeforeRun) => {
          if (!runTime || !hoursBeforeRun) return 'N/A';
          try {
            // Parse the run time
            const [hours, minutes, period] = runTime.match(/(\d+):(\d+)\s*(AM|PM)/).slice(1);
            const runDate = new Date();
            let runHours = parseInt(hours);
            
            // Convert to 24-hour format if PM
            if (period === 'PM' && runHours !== 12) runHours += 12;
            if (period === 'AM' && runHours === 12) runHours = 0;
            
            runDate.setHours(runHours);
            runDate.setMinutes(parseInt(minutes));
            
            // Subtract the hours before run
            const applicationTime = new Date(runDate.getTime() - (parseFloat(hoursBeforeRun) * 60 * 60 * 1000));
            
            // Format the time back to 12-hour format
            let appHours = applicationTime.getHours();
            const appMinutes = applicationTime.getMinutes();
            const appPeriod = appHours >= 12 ? 'PM' : 'AM';
            
            // Convert back to 12-hour format
            if (appHours > 12) appHours -= 12;
            if (appHours === 0) appHours = 12;
            
            return `${appHours}:${appMinutes.toString().padStart(2, '0')} ${appPeriod}`;
          } catch (e) {
            console.error('Error calculating application time:', e);
            return 'N/A';
          }
        };

  const generatePDF = async () => {
    try {
     
      if (!savedHorses || savedHorses.length === 0) {
        setError('No horses available to generate PDF');
        return;
      }

      // Initialize PDF document
      const doc = new jsPDF();
      
      // Process each horse
      savedHorses.forEach((horse, index) => {
        if (index > 0) {
          doc.addPage();
          try {
            doc.addImage('/logo.png', 'PNG', doc.internal.pageSize.getWidth() - 65, 10, 45, 34);
          } catch (error) {
            console.error('Error adding logo to new page:', error);
          }
        }

        // Add logo to first page
        try {
          doc.addImage('/logo.png', 'PNG', doc.internal.pageSize.getWidth() - 65, 10, 45, 34);
        } catch (error) {
          console.error('Error adding logo:', error);
        }

        let currentY = 30;

        // Set up document styles
        doc.setFontSize(26);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(41, 69, 141); // #29458d (navy blue from logo)
        
        // Measure text width
        const titleText = 'Pre Go Prep Schedule';
        const titleWidth = doc.getStringUnitWidth(titleText) * 26 / doc.internal.scaleFactor;
        
        // Add title
        doc.text(titleText, 20, currentY, { align: 'left' });
        
        // Add subtle line under title - only as wide as the text
        doc.setDrawColor(226, 232, 240); // Light gray
        doc.setLineWidth(0.5);
        doc.line(20, currentY + 5, 20 + titleWidth, currentY + 5);
        
        currentY += 30;

        // Horse Card Header with improved formatting
        doc.setFillColor(248, 250, 252);
        // Add shadow effect
        doc.setDrawColor(200, 200, 200);
        doc.roundedRect(16, currentY + 1, doc.internal.pageSize.getWidth() - 32, 120, 4, 4, 'F');
        doc.setDrawColor(203, 213, 225); // Darker border color for better visibility
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(15, currentY, doc.internal.pageSize.getWidth() - 30, 120, 4, 4, 'FD');
        
        // Section Title
        doc.setFontSize(16);
        doc.setTextColor(41, 69, 141);
        doc.setFont('helvetica', 'bold');
        doc.text('Horse & Rider Information', 25, currentY + 15);
        
        // Information Grid Setup with increased spacing
        const startY = currentY + 30;
        const labelX = 45;
        const valueX = 100; // Increased from 95 for better separation
        const rowSpacing = 8.5;
        
        // Information Grid Data
        const infoData = [
          ['Horse Name:', horse.horse_name || 'N/A'],
          ['Rider Name:', horse.rider_name || 'N/A'],
          ['Class Name:', horse.class_name || 'N/A'],
          ['Draw Position:', horse.reverse_order ? 
            `${parseInt(horse.total_runs) - parseInt(horse.draw_number)} (Draw #${horse.draw_number} Reversed)` : 
            String(horse.draw_number || 'N/A')],
          ['Race Name:', horse.race_name || 'N/A'],
          ['Race Start Time:', horse.race_start_time ? 
            (() => {
              // Convert time to 12-hour format with AM/PM
              const [hours, minutes] = horse.race_start_time.split(':');
              const hour = parseInt(hours);
              const ampm = hour >= 12 ? 'PM' : 'AM';
              const hour12 = hour % 12 || 12;
              return `${hour12}:${minutes} ${ampm}`;
            })() : 
            'N/A']
        ];

        // Add Break Information if exists
        if (horse.break_draw_number && horse.break_duration) {
          infoData.push(['Break Information:', `Break at Draw #${horse.break_draw_number} (${horse.break_duration} hours)`]);
        }

        // Add Estimated Go Time with enhanced visibility
        const estimatedGoTime = horse.estimated_run_time ? 
          horse.estimated_run_time.replace(/([AP]M)/, ' $1') : 'N/A';
        infoData.push(['Estimated Go Time:', estimatedGoTime]);

        // Render Information Grid
        let currentRow = 0;
        infoData.forEach(([label, value], index) => {
          const yPos = startY + (currentRow * rowSpacing);
          
          // Add separators
          if (index === 4) { // After Draw Position
            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.2);
            doc.line(labelX, yPos - 4, doc.internal.pageSize.getWidth() - 45, yPos - 4);
          }
          
          // Label (Bold, Darker Gray for better contrast)
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.setTextColor(75, 85, 99); // Darker gray for better contrast
          doc.text(label, labelX, yPos);
          
          // Value formatting with enhanced horse name
          if (label === 'Horse Name:') {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(30, 41, 59);
            doc.text(value, valueX, yPos);
          } else if (label === 'Estimated Go Time:') {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(34, 197, 94);
            doc.text(value, valueX, yPos);
          } else {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(30, 41, 59);
            doc.text(value, valueX, yPos);
          }
          
          currentRow++;
        });

        // Add separator before therapeutic applications
        if (horse.hours_before_run1) {
          const yPos = startY + (currentRow * rowSpacing);
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.2);
          doc.line(labelX, yPos - 4, doc.internal.pageSize.getWidth() - 45, yPos - 4);
        }

        // Therapeutic Applications with improved time formatting
        if (horse.hours_before_run1) {
          const yPos = startY + (currentRow * rowSpacing);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(75, 85, 99);
          doc.text('First Application:', labelX, yPos);
          
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(37, 99, 235);  // #2563eb blue
          const appTime = calculateApplicationTime(horse.estimated_run_time, horse.hours_before_run1);
          const formattedTime = appTime.replace(/([AP]M)/, ' $1'); // Add space before AM/PM
          doc.text(`${formattedTime} (${horse.hours_before_run1} hrs before run)`, valueX, yPos);
          currentRow++;
        }

        // Second Application
        if (horse.hours_before_run2) {
          const yPos = startY + (currentRow * rowSpacing);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(100, 116, 139);
          doc.text('Second Application:', labelX, yPos);
          
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(124, 58, 237);  // #7c3aed purple
          const appTime = calculateApplicationTime(horse.estimated_run_time, horse.hours_before_run2);
          doc.text(`${appTime} (${horse.hours_before_run2} hrs before run)`, valueX, yPos);
          doc.setTextColor(30, 41, 59);  // reset color
          currentRow++;
        }

        // Third Application
        if (horse.hours_before_run3) {
          const yPos = startY + (currentRow * rowSpacing);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(100, 116, 139);
          doc.text('Third Application:', labelX, yPos);
          
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(219, 39, 119);  // #db2777 pink
          const appTime = calculateApplicationTime(horse.estimated_run_time, horse.hours_before_run3);
          doc.text(`${appTime} (${horse.hours_before_run3} hrs before run)`, valueX, yPos);
          doc.setTextColor(30, 41, 59);  // reset color
          currentRow++;
        }

        // Update currentY for next section
        currentY += 100;

        // Add disclaimer at the bottom
        const pageHeight = doc.internal.pageSize.height;
        const pageWidth = doc.internal.pageSize.getWidth();
        
        // Add subtle separator line above disclaimer
        doc.setDrawColor(226, 232, 240); // Light gray
        doc.setLineWidth(0.5);
        doc.line(20, pageHeight - 45, pageWidth - 20, pageHeight - 45);
        
        doc.setFontSize(8);  // Smaller font for disclaimer
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);  // Muted slate color
        
        const disclaimer = 'Pre Go Prep provides estimated run times and therapeutic application timing as a guideline only and does not guarantee accuracy. These estimates do not replace official performance schedules and should not be solely relied upon for competition planning or therapeutic application. Pre Go Prep is not responsible for missed Performance Go times, miscalculations, or any consequences resulting from the use of these estimates. It is the user\'s sole responsibility to track, monitor, and confirm their official Performance Go time with the event producer.';
        
        // Split disclaimer into multiple lines and center it
        const splitDisclaimer = doc.splitTextToSize(disclaimer, pageWidth - 80);  // 40px margin on each side
        doc.text(splitDisclaimer, pageWidth / 2, pageHeight - 35, { align: 'center' });  // Centered text

        // Add page number below disclaimer
        doc.setFontSize(9);
        doc.text(`Page ${index + 1}`, pageWidth / 2, pageHeight - 15, { align: 'center' });
      });

      // Save the PDF
      const fileName = `Pre_Go_Prep_Schedule_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Error generating PDF');
    }
  };

  return (
    <button
      onClick={generatePDF}
      style={{
        backgroundColor: `var(--primary-color)`,
        color: colors.surface,
        padding: '8px 16px',
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '0.9rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: '100%',
        maxWidth: '250px'
      }}
    >
      Generate PDF
    </button>
  );
};

export default PDFGenerator; 